'use client';

import * as React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  /** Optional extra content under the description (e.g. a reason input). */
  children?: React.ReactNode;
  /** Keeps the confirm button disabled (e.g. until a required reason is typed). */
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
  loading,
  children,
  confirmDisabled,
}: ConfirmDialogProps) {
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange?.(false);
  };

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-fade-in" />
        <AlertDialogPrimitive.Content className={cn(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-full max-w-md p-6 rounded-xl border border-cs-border bg-cs-surface-card shadow-2xl',
          'data-[state=open]:animate-slide-up',
        )}>
          <div className="flex items-start gap-4">
            {variant === 'destructive' && (
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cs-error-bg border border-cs-error-500/30 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-cs-error-400" aria-hidden="true" />
              </div>
            )}
            {variant !== 'destructive' && (
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-cs-warning-bg border border-cs-warning-500/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-cs-warning-400" aria-hidden="true" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <AlertDialogPrimitive.Title className="text-base font-semibold text-cs-text-primary">
                {title}
              </AlertDialogPrimitive.Title>
              {description && (
                <AlertDialogPrimitive.Description className="mt-1 text-sm text-cs-text-muted">
                  {description}
                </AlertDialogPrimitive.Description>
              )}
              {children && <div className="mt-3">{children}</div>}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="secondary" size="sm" onClick={handleCancel} disabled={busy || loading}>
                {cancelLabel}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild>
              <Button
                variant={variant === 'destructive' ? 'destructive' : 'primary'}
                size="sm"
                onClick={handleConfirm}
                loading={busy || loading}
                disabled={confirmDisabled}
              >
                {confirmLabel}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
